import React, {
  useRef,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from "react";
import DrawingSearchPanel from "../DesignerSearchPanel";
import DrawingSnGrid from "../DesignerSnGrid";
import TemplateEtcUi, { UIConfig } from "./TemplateEtcUi";
import { DesignerNode } from "../DesignerTree";
import layout from "./TemplateEtc.json";

interface Props {
  loadData?: any;
  nodes: DesignerNode[];
  cdMenu?: string;
}
export interface LayoutConfig {
  layout: UIConfig[];
}

const TemplateEtc = forwardRef(({ loadData, nodes, cdMenu }: Props, ref) => {
  const searchRef = useRef<any>(null);
  const [formData, setFormData] = useState<any>();
  const [formControlData, setFormControlData] = useState<any[]>([]);
  const gridRefs = useRef<{ [key: string]: React.RefObject<any> }>({});
  const [columns, setColumns] = useState<any>({});

  const [dataSources, setDataSources] = useState<any>({});
  const [settings, setSettings] = useState<any>({});
  const [headerInfo, setHeaderInfo] = useState<any>({});
  const formRefs = useRef<{ [key: string]: React.RefObject<any> }>({});
  const [formControls, setFormControls] = useState<any>({});
  const [formDatas, setFormDatas] = useState<any>({});
  const [tabKeys, setTabKeys] = useState<any[]>([]);

  const [layoutConfig, setLayoutConfig] = useState<LayoutConfig>({
    layout: [],
  });

  useEffect(() => {
    if (loadData) {
      if (loadData.layoutConfig) {
        setLayoutConfig(loadData.layoutConfig);
      }
      if (loadData.settings) {
        setSettings(loadData.settings);
      }
      if (loadData.columns) {
        setColumns(loadData.columns);
      }
      if (loadData.headerInfo) {
        setHeaderInfo(loadData.headerInfo);
      }
      if (loadData.formControls) {
        setFormControls(loadData.formControls);
      }
      if (loadData.formData) {
        setFormData(loadData.formData);
      }
      if (loadData.formControlData) {
        setFormControlData(loadData.formControlData);
      }
    }
  }, [loadData]);

  const createLayout = () => {
    setLayoutConfig({ layout: [] });
    nodes.forEach((node) => {
      switch (node.type) {
        case "SearchPanel":
          addSearchPanel(node.type);
          break;
        case "maindiv":
          const maindivLayout = node.layout || "row";
          const maindiv: UIConfig = {
            id: node.title,
            type: "div",
            className:
              maindivLayout == "row"
                ? "flex-1 min-h-0 space-y-1.5"
                : "w-full flex h-full min-h-0 space-x-1.5",
            children: [],
          };
          setLayoutConfig((prev) => ({
            layout: [...prev.layout, maindiv],
          }));
          const maindivChildren = nodes
            .filter((c) => c.parentId === node.id)
            .sort((a, b) => a.order - b.order);
          createChildren(maindivChildren, maindivLayout || "row");
          break;

        default:
          console.warn("알 수 없는 타입", node);
      }
    });
  };

  useEffect(() => {
    setTabKeys([]);
    createLayout();
  }, [nodes, settings]);

  useImperativeHandle(ref, () => ({
    getSaveData,
  }));

  const getSaveData = () => {
    const formControlData = searchRef.current?.getControls();

    const rtn = {
      layoutConfig: layoutConfig,
      columns: columns,
      formControls: formControls,
      settings: settings,
      headerInfo: headerInfo,
      formData: formData,
      formControlData: formControlData,
    };
    return rtn;
  };

  const addSearchPanel = (key: string) => {
    const newDiv: UIConfig = {
      id: "seaerchdiv",
      type: "div",
      className: "mb-1",
      children: [
        {
          type: "SearchPanel",
          props: {
            ref_key: "searchRef",
            search_key: "search",
          },
        },
      ],
    };
    setLayoutConfig((prev) => ({
      layout: [newDiv, ...prev.layout],
    }));
  };

  const setGridSettingDatas = (gridKey: string) => {
    setColumns((prev: Record<string, any[]>) => ({
      ...prev,
      [gridKey]: prev[gridKey]?.length ? prev[gridKey] : [{}],
    }));

    setDataSources((prev: Record<string, any[]>) => ({
      ...prev,
      [gridKey]: [],
    }));

    setHeaderInfo((prev: any) => {
      // 이미 값 있으면 아무것도 안 바꿈 (중요)
      if (prev[gridKey]) return prev;

      return {
        ...prev,
        [gridKey]: {},
      };
    });

    setSettings((prev: any) => {
      // 이미 값 있으면 아무것도 안 바꿈 (중요)
      if (prev[gridKey]) return prev;

      return {
        ...prev,
        [gridKey]: { etcProps: {} },
      };
    });
  };

  const setFormSettingDatas = (formkey: string) => {
    setFormControls((prev: Record<string, any[]>) => ({
      ...prev,
      [formkey]: prev[formkey]?.length ? prev[formkey] : [{}],
    }));

    setFormDatas((prev: Record<string, any[]>) => ({
      ...prev,
      [formkey]: [],
    }));

    setHeaderInfo((prev: any) => {
      // 이미 값 있으면 아무것도 안 바꿈 (중요)
      if (prev[formkey]) return prev;

      return {
        ...prev,
        [formkey]: {},
      };
    });

    setSettings((prev: any) => {
      // 이미 값 있으면 아무것도 안 바꿈 (중요)
      if (prev[formkey]) return prev;

      return {
        ...prev,
        [formkey]: { etcProps: {} },
      };
    });
  };

  const getGridlayout = (gridKey: string, isTab: boolean) => {
    let etcProps = {};

    const sourceEtcProps = settings?.[gridKey]?.etcProps || {};

    if (!isTab) {
      etcProps = sourceEtcProps; // 전체 사용
    } else {
      const { title, ...rest } = sourceEtcProps;
      etcProps = rest; // title 제거된 나머지
    }
    const newGrid: UIConfig = {
      type: "SnGrid",
      id: gridKey,
      props: {
        grid_key: gridKey,
        ref_key: gridKey,
        etcProps: etcProps,
        ...(settings[gridKey] &&
          "filter" in settings[gridKey] && {
            filter: settings[gridKey].filter,
          }),
      },
    };
    return newGrid;
  };

  const getFormlayout = (formkey: string, isTab: boolean) => {
    let etcProps = {};

    const sourceEtcProps = settings?.[formkey]?.etcProps || {};

    if (!isTab) {
      etcProps = sourceEtcProps; // 전체 사용
    } else {
      const { title, ...rest } = sourceEtcProps;
      etcProps = rest; // title 제거된 나머지
    }
    const newForm: UIConfig = {
      type: "SnForm",
      id: formkey,
      props: {
        form_key: formkey,
        ref_key: formkey,
        etcProps: etcProps,
        ...(settings[formkey] &&
          "gridref" in settings[formkey] && {
            gridref: settings[formkey].gridref,
          }),
      },
    };
    return newForm;
  };

  const addGridDiv = (node: DesignerNode, size: string, layout: string) => {
    const index = node.title.replace("그리드", "");
    const gridKey = `grid${index}`;
    const reSizeKey = `reSize${node.id}`;
    setGridSettingDatas(gridKey);

    if (!gridRefs.current[gridKey]) {
      gridRefs.current[gridKey] = React.createRef();
    }
    const grid = getGridlayout(gridKey, false);
    const newGrid: UIConfig = {
      type: "SnResizeBar",
      id: reSizeKey,
      props: {
        id: reSizeKey,
        direction: layout == "row" ? "height" : "width",
        orgSize: size + "%",
      },
      children: [grid],
    };
    const parentId = nodes.find((n) => n.id === node.parentId)?.title || "";
    setLayoutConfig((prev) => ({
      ...prev,
      layout: addChildToNode(prev.layout, parentId, newGrid),
    }));
  };

  const addFileUpload = (node: DesignerNode) => {
    const index = node.title.replace("파일업로드", "");
    const filekey = `fileUpload${index}`;

    const newForm: UIConfig = {
      type: "SnFileUploadBox",
      id: filekey,
      props: {
        fileUplaod_key: filekey,
      },
    };
    const parentId = nodes.find((n) => n.id === node.parentId)?.title || "";
    setLayoutConfig((prev) => ({
      ...prev,
      layout: addChildToNode(prev.layout, parentId, newForm),
    }));
  };

  const addFormDiv = (node: DesignerNode, size: string, layout: string) => {
    const index = node.title.replace("프리폼", "");
    const formkey = `freeform${index}`;
    const reSizeKey = `reSize${node.id}`;
    setFormSettingDatas(formkey);

    if (!formRefs.current[formkey]) {
      formRefs.current[formkey] = React.createRef();
    }
    const form = getFormlayout(formkey, false);
    const newForm: UIConfig = {
      type: "SnResizeBar",
      id: reSizeKey,
      props: {
        id: reSizeKey,
        direction: layout == "row" ? "height" : "width",
        orgSize: size + "%",
      },
      children: [form],
    };
    const parentId = nodes.find((n) => n.id === node.parentId)?.title || "";
    setLayoutConfig((prev) => ({
      ...prev,
      layout: addChildToNode(prev.layout, parentId, newForm),
    }));
  };

  const addTabDiv = (node: DesignerNode, size: string, layout: string) => {
    const index = node.title.replace("탭", "");
    const tabkey = `tab${index}`;
    const reSizeKey = `reSize${node.id}`;
    const pages = nodes.filter((c) => c.parentId === node.id);
    let pageTitles = pages.map((p) => p.title);
    const newTabKeys: string[] = [];
    const bodys = pages.map((p, i) => {
      if (p.subType == "SnGrid") {
        const index = p.subTitle?.replace("그리드", "");
        const gridKey = `grid${index}`;
        newTabKeys.push(gridKey);
        setGridSettingDatas(gridKey);

        if (!gridRefs.current[gridKey]) {
          gridRefs.current[gridKey] = React.createRef();
        }

        const newGrid = getGridlayout(gridKey, true);
        if (settings?.[gridKey]?.etcProps?.title)
          pageTitles[i] = settings[gridKey].etcProps.title;
        return newGrid;
      } else {
        const index = p.subTitle?.replace("프리폼", "");
        const formkey = `freeform${index}`;
        newTabKeys.push(formkey);
        setFormSettingDatas(formkey);

        if (!formRefs.current[formkey]) {
          formRefs.current[formkey] = React.createRef();
        }

        const newForm = getFormlayout(formkey, true);
        if (settings?.[formkey]?.etcProps?.title)
          pageTitles[i] = settings[formkey].etcProps.title;
        return newForm;
      }
    });
    setTabKeys((prev) => Array.from(new Set([...prev, ...newTabKeys])));
    const tabHeader = newTabKeys.map((key) => {
      if (headerInfo[key] !== undefined) {
        const newHeadr: UIConfig = {
          type: "SnTabHeader",
          id: key + "_header",
          props: {
            header_key: key,
          },
        };

        return newHeadr;
      } else {
        const newHeadr: UIConfig = {
          type: "div",
          id: key + "_header",
        };

        return newHeadr;
      }
    });

    const newTab: UIConfig = {
      type: "SnResizeBar",
      id: reSizeKey,
      props: {
        id: reSizeKey,
        direction: layout == "row" ? "height" : "width",
        orgSize: size + "%",
      },
      children: [
        {
          type: "SnTab",
          id: tabkey,
          body: bodys,
          header: tabHeader,
          props: {
            titles: pageTitles,
          },
        },
      ],
    };

    const parentId = nodes.find((n) => n.id === node.parentId)?.title || "";
    setLayoutConfig((prev) => ({
      ...prev,
      layout: addChildToNode(prev.layout, parentId, newTab),
    }));
  };

  const addDiv = (
    node: DesignerNode,
    size: string,
    layout: string,
    divLayout: string
  ) => {
    const index = node.title.replace("div", "");
    const reSizeKey = `reSize${node.id}`;

    const newDiv: UIConfig = {
      type: "SnResizeBar",
      id: reSizeKey,
      props: {
        id: reSizeKey,
        direction: layout == "row" ? "height" : "width",
        orgSize: size + "%",
      },
      children: [
        {
          id: node.title,
          type: "div",
          className:
            divLayout == "row"
              ? "h-full flex-1 min-h-0 space-y-1.5"
              : "w-full flex h-full min-h-0 space-x-1.5",
          children: [],
        },
      ],
    };
    const parentId = nodes.find((n) => n.id === node.parentId)?.title || "";
    setLayoutConfig((prev) => ({
      ...prev,
      layout: addChildToNode(prev.layout, parentId, newDiv),
    }));
  };

  const addChildToNode = (
    items: UIConfig[],
    parentId: string,
    newChild: UIConfig
  ): UIConfig[] => {
    return items.map((item) => {
      if (item.id === parentId) {
        return {
          ...item,
          children: [...(item.children ?? []), newChild],
        };
      }

      if (item.children && item.children.length > 0) {
        return {
          ...item,
          children: addChildToNode(item.children, parentId, newChild),
        };
      }

      return item;
    });
  };

  const createChildren = (childnodes: DesignerNode[], layout: string) => {
    const validNodes = childnodes.filter(
      (node) => node.type !== "SnFileUploadBox"
    );

    const uploadBoxes = childnodes.filter(
      (node) => node.type === "SnFileUploadBox"
    );

    let size =
      100 / validNodes.length -
      validNodes.length * 0.2 -
      uploadBoxes.length * 4;

    if (layout === "column") {
      size =
        100 / validNodes.length -
        validNodes.length * 0.05 -
        uploadBoxes.length * 4;
    }
    childnodes.forEach((childnode) => {
      switch (childnode.type) {
        case "div":
          const divLayout = childnode.layout || "row";
          addDiv(childnode, String(size), layout, divLayout);
          const divChildren = nodes
            .filter((c) => c.parentId === childnode.id)
            .sort((a, b) => a.order - b.order);
          createChildren(divChildren, divLayout);
          break;
        case "SnGrid":
          addGridDiv(childnode, String(size), layout);
          break;
        case "SnTab":
          addTabDiv(childnode, String(size), layout);
          break;
        case "SnForm":
          addFormDiv(childnode, String(size), layout);
          break;
        case "SnFileUploadBox":
          addFileUpload(childnode);
          break;
        default:
          console.warn("알 수 없는 타입", childnode);
      }
    });
  };

  const grids = React.useMemo(() => {
    const result: Record<
      string,
      { columns: any[]; dataSource: any[]; events: any }
    > = {};

    Object.keys(columns).forEach((key) => {
      result[key] = {
        columns: columns[key] ?? [],
        dataSource: dataSources[key] ?? [],
        events: {
          onColumnConfigChanged: (value: any[]) => {
            const updatedcolumns = value.map((ctrl: any, index: number) => ({
              ...ctrl, // 기존 속성 복사
              cd_menu: cdMenu, // cdMenu 추가/수정
              cd_grid: key,
            }));
            console.log(updatedcolumns);
            setColumns((prev: Record<string, any[]>) => ({
              ...prev,
              [key]: updatedcolumns,
            }));
          },
          onHeaderChanged: (value: any) => {
            const updatedControls = value.controls.map(
              (ctrl: any, index: number) => ({
                ...ctrl, // 기존 속성 복사
                cd_menu: cdMenu, // cdMenu 추가/수정
                no_line: index + 1, // 아까 질문하신 no_line 순번 부여 (필요 시)
                cd_form: key,
              })
            );
            setHeaderInfo((prev: Record<string, any[]>) => ({
              ...prev,
              [key]: { controls: updatedControls, data: value.data },
            }));
          },
          onSettingChanged: (value: any, columns: any[]) => {
            setSettings((prev: Record<string, any>) => ({
              ...prev,
              [key]: value,
            }));
          },
        },
        ...(headerInfo[key] && {
          headerInfo: headerInfo[key],
        }),
        ...(settings[key] && {
          initSetting: settings[key],
        }),
      };
    });
    return result;
  }, [columns, layoutConfig, settings]);

  const forms = React.useMemo(() => {
    const result: Record<
      string,
      { controls: any[]; data: any[]; events: any }
    > = {};

    Object.keys(formControls).forEach((key) => {
      result[key] = {
        controls: formControls[key] ?? [],
        data: formDatas[key] ?? [],
        events: {
          onColumnConfigChanged: (value: any[]) => {
            const updatedControls = value.map((ctrl: any, index: number) => ({
              ...ctrl, // 기존 속성 복사
              cd_menu: cdMenu, // cdMenu 추가/수정
              no_line: index + 1, // 아까 질문하신 no_line 순번 부여 (필요 시)
              no_seq: index + 1,
              cd_form: key,
            }));
            setFormControls((prev: Record<string, any[]>) => ({
              ...prev,
              [key]: updatedControls,
            }));
          },
          onHeaderChanged: (value: any) => {
            const updatedControls = value.controls.map(
              (ctrl: any, index: number) => ({
                ...ctrl, // 기존 속성 복사
                cd_menu: cdMenu, // cdMenu 추가/수정
                no_line: index + 1, // 아까 질문하신 no_line 순번 부여 (필요 시)
                cd_form: key,
              })
            );
            setHeaderInfo((prev: Record<string, any[]>) => ({
              ...prev,
              [key]: { controls: updatedControls, data: value.data },
            }));
          },
          onSettingChanged: (value: any) => {
            setSettings((prev: Record<string, any>) => ({
              ...prev,
              [key]: value,
            }));
          },
        },
        ...(headerInfo[key] && {
          headerInfo: headerInfo[key],
        }),
        ...(settings[key] && {
          initSetting: settings[key],
        }),
      };
    });
    return result;
  }, [formControls, layoutConfig, settings]);

  const headers = React.useMemo(() => {
    const result: Record<string, any> = {};

    tabKeys.forEach((key) => {
      result[key] = {
        headerInfo: headerInfo[key] ?? {},
        onBtnEtcClick: (id: string) => {
          alert("header btn etc click :" + id);
        },
      };
    });
    return result;
  }, [tabKeys, layoutConfig]);

  const search = useMemo(
    () => ({
      data: formData,
      controls: formControlData,
      onValueChanged: (value: any) => {
        const updatedControls = value.controls.map(
          (ctrl: any, index: number) => ({
            ...ctrl, // 기존 속성 복사
            cd_menu: cdMenu, // cdMenu 추가/수정
            no_line: index + 1, // 아까 질문하신 no_line 순번 부여 (필요 시)
            cd_form: "search",
          })
        );
        setFormControlData(updatedControls);
        setFormData(value.data);
      },
    }),
    [formData, formControlData]
  );

  const dynamicProps = React.useMemo(
    () => ({
      // 1. Ref (JSON의 "ref"와 매칭)
      searchRef,
      // 2. State & Data (JSON의 "_key"와 매칭)
      search,
      grids,
      forms,
      headers,
    }),
    [
      // Data State가 변경될 때만 dynamicProps가 새로 생성되도록 의존성 지정
      search,
      forms,
      headers,
    ]
  );

  return (
    <TemplateEtcUi dynamicProps={dynamicProps} layoutConfig={layoutConfig} />
  );
});

export default TemplateEtc;
